import { useGetMenuFormPermissions } from '@/api/permission';
import { useToast } from '@/hooks/use-toast';
import { menuItems } from '@/utils/constant';
import { MenuItem } from '@/utils/types/header';
import { MenuFormPermission } from '@/utils/types/permission';
import { useEffect, useState } from 'react';

interface UseMenuPermissionsReturn {
  menuItems: MenuItem[];
  isLoading: boolean;
  error: Error | null;
  canView: (key: string) => boolean;
}

const useMenuFormPermissions = (): UseMenuPermissionsReturn => {
  const [filteredMenu, setFilteredMenu] = useState<MenuItem[]>([]);
  const [allowedKeys, setAllowedKeys] = useState<Set<string>>(new Set());
  const { isLoading, data, error } = useGetMenuFormPermissions();
  const { toast } = useToast();

  const filterMenuByPermissions = (menu: MenuItem[], permissionsData: MenuFormPermission[]) => {
    // Create a Set of allowed keys from permissions for faster lookup
    const allowedKeys = new Set<string>();

    permissionsData.forEach((permission) => {
      if (permission.isView) {
        allowedKeys.add(permission.dbFormName);
      }
    });

    // Filter menu items based on permissions
    const filteredMenuItems = menu
      .map((menuItem) => {
        // Filter submenu items based on key field
        const filteredSubmenu = menuItem?.submenu?.filter((subItem) => {
          // If key is empty, always show the item (permission granted by default)
          if (!subItem.key || subItem.key === '') {
            return true;
          }

          // Otherwise, check if the key is in allowed keys
          return allowedKeys.has(subItem.key);
        });

        // Only include menu item if it has submenu items after filtering
        if (filteredSubmenu && filteredSubmenu?.length > 0) {
          return {
            ...menuItem,
            submenu: filteredSubmenu,
          };
        }

        return null;
      })
      .filter((item) => item !== null) as MenuItem[];

    return filteredMenuItems;
  };

  const canView = (key: string): boolean => {
    // If key is empty, always return true (permission granted by default)
    if (!key || key === '') {
      return true;
    }

    // Check if the key exists in allowed keys
    return allowedKeys.has(key);
  };

  useEffect(() => {
    const showAllMenusAsFallback = () => {
      setAllowedKeys(new Set());
      setFilteredMenu(menuItems);
    };

    const hideAllMenus = () => {
      setAllowedKeys(new Set());
      setFilteredMenu([]);
    };

    // If there's an error, show all menu items as fallback
    if (error) {
      console.error('Failed to fetch menu permissions, showing all menu items as fallback');
      showAllMenusAsFallback();
      return;
    }

    // If we have valid permissions data
    if (data?.data && data?.data?.length > 0) {
      const permissions = data.data;

      const keys = new Set<string>();
      permissions.forEach((permission) => {
        if (permission.isView) {
          keys.add(permission.dbFormName);
        }
      });
      setAllowedKeys(keys);
      const filtered = filterMenuByPermissions(menuItems, permissions);
      setFilteredMenu(filtered);
    } else if (data && data.status === true && Array.isArray(data.data)) {
      // API succeeded but returned empty permissions - user has no permissions
      hideAllMenus();
    } else if (!data) {
      // Still loading
      hideAllMenus();
    } else {
      // Any other case (API failure, etc) - show all as fallback
      showAllMenusAsFallback();
    }
  }, [data, error]);

  useEffect(() => {
    if (error) {
      toast({
        title: 'Error',
        description: 'Failed to fetch menu permissions',
        variant: 'destructive',
      });
    }
  }, [error]);

  return {
    menuItems: filteredMenu,
    isLoading,
    error,
    canView,
  };
};

export default useMenuFormPermissions;
